import {
  CommentAnnotation,
  Positioned,
  RectangleAnnotation,
  RenderedAnnotation,
  renderToRenderedPage,
  StampAnnotation,
  TextAnnotation
} from '@st/document-rendering'
import type { DocumentAnnotation } from '@st/sdk'
import { match } from '@st/pattern'
import { CalculatorTapeView } from './calculator-tape-view'
import { StampView } from './stamp-view'

export type RenderableDocumentAnnotation = {
  annotation: DocumentAnnotation
  author?: string
}

type DocumentAnnotationValue = DocumentAnnotation extends infer Annotation
  ? Annotation extends DocumentAnnotation
    ? Omit<Annotation, 'id' | 'time'> & Partial<Pick<Annotation, 'id' | 'time'>>
    : never
  : never

export function DocumentAnnotationElement({
  annotation,
  author
}: {
  annotation: DocumentAnnotationValue
  author?: string
}) {
  const common = {
    id: annotation.id,
    author,
    timestamp: annotation.time
  }

  return match(annotation)
    .with({ type: 'rectangle' }, (rectangle) => (
      <RectangleAnnotation
        {...common}
        border={{
          color: rectangle.border.color ?? '#000000',
          width: rectangle.border.width,
          radius: rectangle.border.radius
        }}
      />
    ))
    .with({ type: 'text' }, (text) => (
      <TextAnnotation {...common} content={text.content} color={text.color} font={text.font} />
    ))
    .with({ type: 'comment' }, (comment) => (
      <CommentAnnotation {...common} content={comment.content} />
    ))
    .with({ type: 'stamp' }, (stamp) => (
      <StampAnnotation {...common}>
        <StampView annotation={stamp} />
      </StampAnnotation>
    ))
    .with({ type: 'calculator_tape' }, (calculatorTape) => (
      <StampAnnotation {...common}>
        <CalculatorTapeView annotation={calculatorTape} />
      </StampAnnotation>
    ))
    .exhaustive()
}

export function renderDocumentAnnotation(
  annotation: DocumentAnnotation,
  author?: string
): RenderedAnnotation {
  const page = renderToRenderedPage(
    <Positioned bounds={annotation.bounds}>
      <DocumentAnnotationElement annotation={annotation} author={author} />
    </Positioned>,
    {
      width: annotation.bounds.x + annotation.bounds.width,
      height: annotation.bounds.y + annotation.bounds.height
    }
  )

  if (page.annotations.length != 1) {
    throw new Error(`Expected one rendered annotation, received ${page.annotations.length}`)
  }

  return page.annotations[0]
}
